import { useEffect, useState, useCallback, useRef } from "react";
import * as Icons from "lucide-react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const TYPE_TONE = {
  client_facing: "bg-blue-50 text-blue-700",
  internal: "bg-violet-50 text-violet-700",
  future: "bg-slate-100 text-slate-400",
};
const TYPE_LABEL = { client_facing: "Client-Facing", internal: "Internal Operations", future: "Future" };
const STATUS_TONE = { ready: "bg-amber-50 text-amber-700", connected: "bg-emerald-50 text-emerald-700", disabled: "bg-slate-100 text-slate-400" };
const STATUS_LABEL = { ready: "Ready · No LLM", connected: "LLM Connected", disabled: "Disabled" };

export default function AIAgents() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canManage = ["administrator", "partner"].includes(user?.role);
  const [agents, setAgents] = useState([]);
  const [opts, setOpts] = useState({ llm_options: [], memory_options: [] });
  const [selected, setSelected] = useState(null);
  const [chaseTab, setChaseTab] = useState("chat"); // "chat" | "config"
  const [adminOpen, setAdminOpen] = useState(false);

  const load = useCallback(() => api.get("/ai-agents").then((r) => {
    setAgents(r.data.agents); setOpts({ llm_options: r.data.llm_options, memory_options: r.data.memory_options });
    setSelected((s) => s ? r.data.agents.find((a) => a.key === s.key) || null : null);
  }).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const handleSelectAgent = (a) => {
    setSelected(a);
    if (a.key === "marketing_ai") setChaseTab("chat");
  };

  return (
    <div className="p-6 max-w-[1200px] mx-auto" data-testid="ai-agents-page">
      {/* Workspace tab bar */}
      <div className="flex items-center gap-0.5 border-b border-slate-200 pb-0 mb-5 -mt-1" data-testid="ai-workspace-tabs">
        <button data-testid="ws-tab-agents" onClick={() => navigate("/ai/agents")}
          className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 border-blue-600 text-blue-700 transition-colors">
          <Icons.Bot className="h-4 w-4" />AI Agents
        </button>
        <button data-testid="ws-tab-operations" onClick={() => navigate("/ai/operations")}
          className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 border-transparent text-slate-600 hover:text-slate-900 hover:border-slate-300 transition-colors">
          <Icons.HeartPulse className="h-4 w-4" />AI Operations
        </button>
        <button data-testid="ws-tab-lead-recovery" onClick={() => navigate("/ai/lead-recovery")}
          className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 border-transparent text-slate-600 hover:text-slate-900 hover:border-slate-300 transition-colors">
          <Icons.Zap className="h-4 w-4" />Lead Recovery
        </button>
        <div className="relative ml-auto">
          <button data-testid="ws-tab-admin" onClick={() => setAdminOpen((v) => !v)}
            className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${adminOpen ? "border-slate-700 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300"}`}>
            <Icons.Settings2 className="h-4 w-4" />Administration
            <Icons.ChevronDown className={`h-3.5 w-3.5 transition-transform ${adminOpen ? "rotate-180" : ""}`} />
          </button>
          {adminOpen && (
            <div className="absolute right-0 top-full mt-1 w-64 bg-white border border-slate-200 rounded-xl shadow-lg z-20 p-2" data-testid="ai-admin-panel">
              <button data-testid="ai-admin-link-knowledge" onClick={() => { setAdminOpen(false); navigate("/ai/knowledge"); }}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-slate-700 hover:bg-slate-50 transition-colors">
                <Icons.Library className="h-4 w-4 text-slate-400 shrink-0" />
                <div className="text-left"><div className="font-medium">AI Knowledge</div><div className="text-xs text-slate-400">Manage knowledge sources for AI agents</div></div>
              </button>
              <button data-testid="ai-admin-link-workflow" onClick={() => { setAdminOpen(false); navigate("/ai/workflow-testing"); }}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-slate-700 hover:bg-slate-50 transition-colors">
                <Icons.FlaskConical className="h-4 w-4 text-slate-400 shrink-0" />
                <div className="text-left"><div className="font-medium">Workflow Testing</div><div className="text-xs text-slate-400">QA harness for AI workflows</div></div>
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center gap-3 mb-6">
        <div className="h-10 w-10 rounded-xl bg-blue-600 flex items-center justify-center"><Icons.Bot className="h-5 w-5 text-white" /></div>
        <div>
          <p className="text-xs text-slate-400 uppercase tracking-wide">AI Center</p>
          <h1 className="font-heading text-2xl font-bold text-slate-900">AI Agents</h1>
          <p className="text-sm text-slate-500">Tax Flow's AI Agent Framework — specialized assistants with distinct audiences, permission boundaries and (soon) models.</p>
        </div>
      </div>

      <div className="bg-blue-50/60 border border-blue-100 rounded-xl p-3.5 mb-5 text-sm text-blue-800 flex items-start gap-2" data-testid="ai-agents-banner">
        <Icons.Info className="h-4 w-4 mt-0.5 shrink-0" />
        <span>
          <strong>Penny</strong> serves clients (within each client's permission boundary),{" "}
          <strong>Allison</strong> is the internal staff co-pilot (inherits your RBAC, never elevates), and{" "}
          <strong>CHASE</strong> is the Marketing &amp; Business Development Intelligence Agent (advisory/draft mode — internal staff only).
          All three are <strong>connected to Claude</strong>. Grant them knowledge in <strong>AI Knowledge</strong>.
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {agents.map((a) => {
          const Ic = Icons[a.avatar] || Icons.Bot;
          return (
            <button key={a.key} onClick={() => handleSelectAgent(a)} data-testid={`agent-card-${a.key}`}
              className={`text-left bg-white rounded-xl border p-4 transition-all hover:shadow-sm ${a.type === "future" ? "opacity-70" : ""} ${selected?.key === a.key ? "border-blue-400 ring-1 ring-blue-200" : "border-slate-200"}`}>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className={`h-11 w-11 rounded-xl flex items-center justify-center ${a.type === "client_facing" ? "bg-blue-100 text-blue-600" : a.type === "internal" ? "bg-violet-100 text-violet-600" : "bg-slate-100 text-slate-400"}`}><Ic className="h-5 w-5" /></div>
                  <div>
                    <div className="font-heading font-semibold text-slate-800">{a.name}</div>
                    <div className="text-xs text-slate-500">{a.tagline}</div>
                  </div>
                </div>
                <span className={`h-2.5 w-2.5 rounded-full mt-1 ${a.enabled ? "bg-emerald-500" : "bg-slate-300"}`} title={a.enabled ? "Enabled" : "Disabled"} />
              </div>
              <div className="flex items-center gap-1.5 mt-3 flex-wrap">
                <span className={`text-[10px] px-2 py-0.5 rounded-full ${TYPE_TONE[a.type]}`}>{TYPE_LABEL[a.type]}</span>
                <span className={`text-[10px] px-2 py-0.5 rounded-full ${STATUS_TONE[a.status]}`}>{STATUS_LABEL[a.status]}</span>
                {a.preferred_llm && a.type !== "future" && <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 capitalize">{a.preferred_llm}</span>}
                {a.key === "marketing_ai" && <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-50 text-amber-700">Advisory Only</span>}
              </div>
            </button>
          );
        })}
      </div>

      {selected && selected.key === "marketing_ai" ? (
        <div className="mt-6" data-testid="chase-workspace">
          <div className="flex items-center gap-2 border-b border-slate-200 mb-4">
            <button data-testid="chase-tab-chat" onClick={() => setChaseTab("chat")}
              className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${chaseTab === "chat" ? "border-violet-600 text-violet-700" : "border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300"}`}>
              <Icons.MessageSquare className="h-4 w-4" />Chat with CHASE
            </button>
            <button data-testid="chase-tab-config" onClick={() => setChaseTab("config")}
              className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${chaseTab === "config" ? "border-slate-700 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300"}`}>
              <Icons.Settings2 className="h-4 w-4" />Configuration
            </button>
            <button onClick={() => setSelected(null)} className="ml-auto text-slate-400 hover:text-slate-700 mr-1"><Icons.X className="h-5 w-5" /></button>
          </div>
          {chaseTab === "chat"
            ? <ChaseChatPanel agent={selected} onClose={() => setSelected(null)} />
            : <AgentEditor key={selected.key} agent={selected} opts={opts} canManage={canManage} onClose={() => setSelected(null)} onSaved={load} />}
        </div>
      ) : selected ? (
        <AgentEditor key={selected.key} agent={selected} opts={opts} canManage={canManage} onClose={() => setSelected(null)} onSaved={load} />
      ) : null}
    </div>
  );
}

const inp = "w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200";
const lbl = "text-xs font-medium text-slate-500 mb-1 block";
const csv = (arr) => (arr || []).join(", ");
const toArr = (s) => (s || "").split(",").map((x) => x.trim()).filter(Boolean);

function AgentEditor({ agent, opts, canManage, onClose, onSaved }) {
  const isFuture = agent.type === "future";
  const ro = !canManage || isFuture;
  const [f, setF] = useState({
    ...agent,
    allowed_tools: csv(agent.allowed_tools), allowed_collections: csv(agent.allowed_collections),
    allowed_actions: csv(agent.allowed_actions), denied_actions: csv(agent.denied_actions), channels: csv(agent.channels),
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const setLc = (k, v) => setF((p) => ({ ...p, lead_capture: { ...(p.lead_capture || {}), [k]: v } }));
  const toggleField = (listKey, field) => setF((p) => {
    const lc = p.lead_capture || {};
    const cur = lc[listKey] || [];
    const next = cur.includes(field) ? cur.filter((x) => x !== field) : [...cur, field];
    return { ...p, lead_capture: { ...lc, [listKey]: next } };
  });

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/ai-agents/${agent.key}`, {
        name: f.name, avatar: f.avatar, tagline: f.tagline, personality_prompt: f.personality_prompt,
        system_prompt: f.system_prompt, sales_strategy_prompt: f.sales_strategy_prompt,
        allowed_tools: toArr(f.allowed_tools), allowed_collections: toArr(f.allowed_collections),
        allowed_actions: toArr(f.allowed_actions), denied_actions: toArr(f.denied_actions), channels: toArr(f.channels),
        confidence_threshold: Number(f.confidence_threshold), temperature: Number(f.temperature),
        preferred_llm: f.preferred_llm, conversation_memory: f.conversation_memory,
        ...(f.lead_capture ? { lead_capture: f.lead_capture } : {}),
      });
      toast.success(`${f.name} saved`); onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); } finally { setSaving(false); }
  };
  const toggle = async () => {
    try { const { data } = await api.post(`/ai-agents/${agent.key}/toggle`); set("enabled", data.enabled); toast.success(data.enabled ? "Enabled" : "Disabled"); onSaved(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="mt-6 bg-white rounded-xl border border-slate-200 p-5" data-testid="agent-editor">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <h2 className="font-heading font-semibold text-slate-900">{agent.name} — Configuration</h2>
          <span className={`text-[10px] px-2 py-0.5 rounded-full ${TYPE_TONE[agent.type]}`}>{TYPE_LABEL[agent.type]}</span>
        </div>
        <div className="flex items-center gap-2">
          {canManage && !isFuture && <button onClick={toggle} data-testid="agent-toggle" className="text-xs border border-slate-200 rounded-lg px-2.5 py-1.5 text-slate-600 hover:bg-slate-50">{f.enabled ? "Disable" : "Enable"}</button>}
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><Icons.X className="h-5 w-5" /></button>
        </div>
      </div>

      {isFuture && <div className="mb-4 text-sm text-slate-500 bg-slate-50 rounded-lg p-3">This agent is reserved for a future release. The registry slot is ready; configuration unlocks when it's built.</div>}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><label className={lbl}>Name</label><input className={inp} value={f.name || ""} onChange={(e) => set("name", e.target.value)} disabled={ro} data-testid="agent-f-name" /></div>
            <div><label className={lbl}>Avatar (icon)</label><input className={inp} value={f.avatar || ""} onChange={(e) => set("avatar", e.target.value)} disabled={ro} /></div>
          </div>
          <div><label className={lbl}>Tagline</label><input className={inp} value={f.tagline || ""} onChange={(e) => set("tagline", e.target.value)} disabled={ro} /></div>
          <div><label className={lbl}>Permission Boundary</label><input className={`${inp} bg-slate-50`} value={agent.permission_boundary === "client" ? "Client permission boundary" : "Staff RBAC (inherits user permissions)"} disabled /></div>
          <div><label className={lbl}>Personality Prompt</label><textarea className={inp} rows={3} value={f.personality_prompt || ""} onChange={(e) => set("personality_prompt", e.target.value)} disabled={ro} data-testid="agent-f-personality" /></div>
          <div><label className={lbl}>System Prompt</label><textarea className={inp} rows={6} value={f.system_prompt || ""} onChange={(e) => set("system_prompt", e.target.value)} disabled={ro} data-testid="agent-f-system" /></div>
          <div><label className={lbl}>Sales & Conversion Strategy Prompt</label><textarea className={inp} rows={4} value={f.sales_strategy_prompt || ""} onChange={(e) => set("sales_strategy_prompt", e.target.value)} disabled={ro} data-testid="agent-f-sales-strategy" placeholder="How this agent should consultatively guide leads/clients toward next steps (book a consult, accept an estimate, sign an engagement) — without being pushy." /></div>
          {f.lead_capture && (
            <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-3 space-y-3" data-testid="agent-lead-capture">
              <div className="flex items-center gap-2">
                <Icons.UserPlus className="h-4 w-4 text-blue-600" />
                <span className="text-sm font-semibold text-slate-800">Lead Capture</span>
              </div>
              <p className="text-xs text-slate-500 -mt-1">Collect contact info conversationally before deep tax qualification, so no lead is lost if the chat ends early.</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={lbl}>Mode</label>
                  <select className={inp} value={f.lead_capture.mode || "recommended"} onChange={(e) => setLc("mode", e.target.value)} disabled={ro} data-testid="lc-mode">
                    <option value="required">Required</option>
                    <option value="recommended">Recommended</option>
                    <option value="disabled">Disabled</option>
                  </select>
                </div>
                <div>
                  <label className={lbl}>Timing</label>
                  <select className={inp} value={f.lead_capture.timing || "early"} onChange={(e) => setLc("timing", e.target.value)} disabled={ro} data-testid="lc-timing">
                    <option value="immediate">Immediate</option>
                    <option value="early">Early (default)</option>
                    <option value="before_quote">Before Quote</option>
                    <option value="before_consultation">Before Consultation</option>
                  </select>
                </div>
              </div>
              <div>
                <label className={lbl}>Required Fields</label>
                <div className="flex flex-wrap gap-3 mt-1">
                  {["first_name", "last_name", "email", "phone"].map((fld) => (
                    <label key={fld} className="flex items-center gap-1.5 text-xs text-slate-600">
                      <input type="checkbox" checked={(f.lead_capture.required_fields || []).includes(fld)} onChange={() => toggleField("required_fields", fld)} disabled={ro} data-testid={`lc-req-${fld}`} />
                      {fld.replace("_", " ")}
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <label className={lbl}>Optional Fields</label>
                <div className="flex flex-wrap gap-3 mt-1">
                  {["first_name", "last_name", "email", "phone"].map((fld) => (
                    <label key={fld} className="flex items-center gap-1.5 text-xs text-slate-600">
                      <input type="checkbox" checked={(f.lead_capture.optional_fields || []).includes(fld)} onChange={() => toggleField("optional_fields", fld)} disabled={ro} data-testid={`lc-opt-${fld}`} />
                      {fld.replace("_", " ")}
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
        <div className="space-y-3">
          <div className="grid grid-cols-3 gap-3">
            <div><label className={lbl}>Preferred LLM</label><select className={inp} value={f.preferred_llm || ""} onChange={(e) => set("preferred_llm", e.target.value)} disabled={ro} data-testid="agent-f-llm"><option value="">—</option>{opts.llm_options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}</select></div>
            <div><label className={lbl}>Temperature</label><input type="number" step="0.1" min="0" max="1" className={inp} value={f.temperature ?? ""} onChange={(e) => set("temperature", e.target.value)} disabled={ro} /></div>
            <div><label className={lbl}>Confidence</label><input type="number" step="0.05" min="0" max="1" className={inp} value={f.confidence_threshold ?? ""} onChange={(e) => set("confidence_threshold", e.target.value)} disabled={ro} /></div>
          </div>
          <div><label className={lbl}>Conversation Memory</label><select className={inp} value={f.conversation_memory || ""} onChange={(e) => set("conversation_memory", e.target.value)} disabled={ro}><option value="">—</option>{opts.memory_options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}</select></div>
          <div><label className={lbl}>Channels (comma-separated)</label><input className={inp} value={f.channels} onChange={(e) => set("channels", e.target.value)} disabled={ro} /></div>
          <div><label className={lbl}>Allowed Tools</label><textarea className={inp} rows={2} value={f.allowed_tools} onChange={(e) => set("allowed_tools", e.target.value)} disabled={ro} /></div>
          <div><label className={lbl}>Allowed Collections</label><textarea className={inp} rows={2} value={f.allowed_collections} onChange={(e) => set("allowed_collections", e.target.value)} disabled={ro} /></div>
          <div><label className={lbl}>Allowed Actions</label><textarea className={inp} rows={2} value={f.allowed_actions} onChange={(e) => set("allowed_actions", e.target.value)} disabled={ro} /></div>
          <div><label className={lbl}>Denied Actions (guardrails)</label><textarea className={inp} rows={2} value={f.denied_actions} onChange={(e) => set("denied_actions", e.target.value)} disabled={ro} /></div>
          <div><label className={lbl}>Escalation Rules</label><textarea className={inp} rows={2} value={f.escalation_rules || ""} onChange={(e) => set("escalation_rules", e.target.value)} disabled={ro} /></div>
        </div>
      </div>

      {canManage && !isFuture && (
        <div className="flex justify-end mt-4">
          <button onClick={save} disabled={saving} data-testid="agent-save" className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-5 py-2 text-sm font-medium disabled:opacity-50">{saving ? <Icons.Loader2 className="h-4 w-4 animate-spin" /> : <Icons.Check className="h-4 w-4" />}Save Configuration</button>
        </div>
      )}
    </div>
  );
}


function ChaseChatPanel({ agent }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId] = useState("default");
  // Client context selector state
  const [ctxSearch, setCtxSearch] = useState("");
  const [ctxResults, setCtxResults] = useState([]);
  const [ctxSearching, setCtxSearching] = useState(false);
  const [ctxClient, setCtxClient] = useState(null); // { id, full_name, lifecycle_status }
  const [ctxOpen, setCtxOpen] = useState(false);
  const messagesEndRef = useRef(null);
  const ctxRef = useRef(null);

  // Load history on mount
  useEffect(() => {
    api.get(`/ai-agents/chase/history?session_id=${sessionId}`)
      .then((r) => setMessages(r.data.messages || []))
      .catch(() => {});
  }, [sessionId]);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Close context dropdown on outside click
  useEffect(() => {
    const handler = (e) => { if (ctxRef.current && !ctxRef.current.contains(e.target)) setCtxOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Debounced client search
  useEffect(() => {
    if (!ctxSearch.trim() || ctxSearch.length < 2) { setCtxResults([]); return; }
    setCtxSearching(true);
    const t = setTimeout(() => {
      api.get(`/crm/contacts?q=${encodeURIComponent(ctxSearch)}&limit=8`)
        .then((r) => { setCtxResults(r.data.contacts || r.data.items || []); setCtxOpen(true); })
        .catch(() => setCtxResults([]))
        .finally(() => setCtxSearching(false));
    }, 300);
    return () => clearTimeout(t);
  }, [ctxSearch]);

  const send = async () => {
    if (!input.trim() || sending) return;
    const userMsg = { role: "user", text: input.trim(), created_at: new Date().toISOString() };
    setMessages((prev) => [...prev, userMsg]);
    setInput("");
    setSending(true);
    try {
      const payload = { message: userMsg.text, session_id: sessionId };
      if (ctxClient) payload.client_id = ctxClient.id;
      const { data } = await api.post("/ai-agents/chase/chat", payload);
      setMessages((prev) => [...prev, { role: "chase", text: data.reply, created_at: new Date().toISOString() }]);
    } catch (e) {
      toast.error(e.response?.data?.detail || "CHASE is unavailable right now");
      setMessages((prev) => prev.slice(0, -1));
      setInput(userMsg.text);
    } finally {
      setSending(false);
    }
  };

  const clearChat = () => { setMessages([]); setCtxClient(null); setCtxSearch(""); };

  const selectCtxClient = (c) => {
    setCtxClient({ id: c.id, full_name: c.full_name, lifecycle_status: c.lifecycle_status });
    setCtxSearch(""); setCtxResults([]); setCtxOpen(false);
  };

  const statusBadge = (s) => {
    const map = { lead: "bg-amber-50 text-amber-700", prospect: "bg-blue-50 text-blue-700", active_client: "bg-emerald-50 text-emerald-700", inactive_client: "bg-slate-100 text-slate-500" };
    return map[s] || "bg-slate-100 text-slate-500";
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200" data-testid="chase-chat-panel">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-lg bg-violet-100 flex items-center justify-center">
            <Icons.Megaphone className="h-4 w-4 text-violet-600" />
          </div>
          <div>
            <span className="font-semibold text-slate-800 text-sm">CHASE</span>
            <span className="ml-2 text-[10px] px-2 py-0.5 rounded-full bg-amber-50 text-amber-700">Advisory · Draft Only</span>
          </div>
        </div>
        <button onClick={clearChat} data-testid="chase-clear-chat" className="text-xs text-slate-400 hover:text-slate-600 flex items-center gap-1">
          <Icons.RotateCcw className="h-3.5 w-3.5" />New Chat
        </button>
      </div>

      {/* Client context selector */}
      <div className="px-4 py-2.5 border-b border-slate-50 bg-slate-50/60" data-testid="chase-context-bar">
        {ctxClient ? (
          <div className="flex items-center gap-2 text-xs">
            <Icons.UserRound className="h-3.5 w-3.5 text-violet-500" />
            <span className="font-medium text-slate-700">{ctxClient.full_name}</span>
            <span className={`px-1.5 py-0.5 rounded-full ${statusBadge(ctxClient.lifecycle_status)}`}>{ctxClient.lifecycle_status?.replace("_", " ")}</span>
            <button onClick={() => { setCtxClient(null); setCtxSearch(""); }} data-testid="chase-ctx-clear" className="ml-auto text-slate-400 hover:text-slate-600"><Icons.X className="h-3.5 w-3.5" /></button>
          </div>
        ) : (
          <div className="relative" ref={ctxRef}>
            <div className="flex items-center gap-1.5">
              <Icons.UserSearch className="h-3.5 w-3.5 text-slate-400 shrink-0" />
              <input
                data-testid="chase-ctx-search"
                className="flex-1 bg-transparent text-xs text-slate-700 placeholder-slate-400 outline-none"
                placeholder="Add client/lead context (optional — search by name)"
                value={ctxSearch}
                onChange={(e) => setCtxSearch(e.target.value)}
                onFocus={() => ctxResults.length > 0 && setCtxOpen(true)}
              />
              {ctxSearching && <Icons.Loader2 className="h-3 w-3 text-slate-400 animate-spin shrink-0" />}
            </div>
            {ctxOpen && ctxResults.length > 0 && (
              <div className="absolute left-0 top-full mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg z-20 max-h-52 overflow-y-auto" data-testid="chase-ctx-results">
                {ctxResults.map((c) => (
                  <button key={c.id} onMouseDown={() => selectCtxClient(c)} data-testid={`chase-ctx-opt-${c.id}`}
                    className="w-full flex items-center justify-between px-3 py-2 hover:bg-slate-50 text-left gap-2">
                    <span className="text-sm font-medium text-slate-800 truncate">{c.full_name}</span>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full shrink-0 ${statusBadge(c.lifecycle_status)}`}>{c.lifecycle_status?.replace("_", " ")}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Messages */}
      <div className="h-80 overflow-y-auto px-4 py-3 space-y-3" data-testid="chase-messages">
        {messages.length === 0 && (
          <div className="h-full flex flex-col items-center justify-center text-center gap-2 text-slate-400" data-testid="chase-empty-state">
            <Icons.Megaphone className="h-8 w-8 text-violet-200" />
            <p className="text-sm font-medium text-slate-500">Ask CHASE for marketing guidance</p>
            <p className="text-xs max-w-xs">Analyze leads, draft follow-ups, recommend next steps, identify overdue outreach. All outputs are advisory — staff review and approve every action.</p>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`chase-msg-${i}`}>
            {m.role !== "user" && (
              <div className="h-6 w-6 rounded-full bg-violet-100 flex items-center justify-center mr-2 mt-0.5 shrink-0">
                <Icons.Megaphone className="h-3 w-3 text-violet-600" />
              </div>
            )}
            <div className={`max-w-[78%] rounded-xl px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${m.role === "user" ? "bg-blue-600 text-white rounded-br-sm" : "bg-slate-50 border border-slate-100 text-slate-800 rounded-bl-sm"}`}>
              {m.text}
            </div>
          </div>
        ))}
        {sending && (
          <div className="flex justify-start" data-testid="chase-typing">
            <div className="h-6 w-6 rounded-full bg-violet-100 flex items-center justify-center mr-2 shrink-0">
              <Icons.Megaphone className="h-3 w-3 text-violet-600" />
            </div>
            <div className="bg-slate-50 border border-slate-100 rounded-xl rounded-bl-sm px-4 py-2.5 flex items-center gap-1.5">
              <span className="h-1.5 w-1.5 bg-violet-400 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
              <span className="h-1.5 w-1.5 bg-violet-400 rounded-full animate-bounce" style={{ animationDelay: "120ms" }} />
              <span className="h-1.5 w-1.5 bg-violet-400 rounded-full animate-bounce" style={{ animationDelay: "240ms" }} />
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="px-4 py-3 border-t border-slate-100" data-testid="chase-input-area">
        <div className="flex items-end gap-2">
          <textarea
            data-testid="chase-input"
            className="flex-1 border border-slate-200 rounded-xl px-3.5 py-2.5 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-violet-200 max-h-32"
            rows={1}
            placeholder="Ask CHASE for a draft, recommendation, or analysis…"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            disabled={sending}
          />
          <button
            data-testid="chase-send-btn"
            onClick={send}
            disabled={!input.trim() || sending}
            className="h-10 w-10 rounded-xl bg-violet-600 hover:bg-violet-700 disabled:opacity-40 flex items-center justify-center transition-colors shrink-0">
            {sending ? <Icons.Loader2 className="h-4 w-4 text-white animate-spin" /> : <Icons.Send className="h-4 w-4 text-white" />}
          </button>
        </div>
        <p className="text-[10px] text-slate-400 mt-1.5 text-center">CHASE outputs are advisory only — all recommendations require staff review and approval.</p>
      </div>
    </div>
  );
}
